import React, { useContext, useState, useEffect, useRef } from 'react';
import VideoController from '@/v2/components/controllers/VideoController/VideoController';
import VideoControllerUtils from '@/v2/components/controllers/VideoController/VideoControllerUtils';
import { StateContext } from '@/v2/state/store';
import shaka from 'shaka-player';
import './video.scss';

const loadVideo = async (videoSrc, param) => {
    const { srcDash, srcHls } = param.props;
    const { refVideo } = param;

    shaka.polyfill.installAll();

    const isAndroid = /(android)/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (srcDash && (shaka.Player.isBrowserSupported() || isAndroid) && !isIOS) {
        const player = new shaka.Player(refVideo);

        // Set default bandwidth estimate so that
        // videos are sharp on initial load
        player.configure('abr.defaultBandwidthEstimate', 50000000); // 50Mbit

        await player.load(srcDash);
        return player;
    }

    if (srcHls && refVideo.canPlayType('application/vnd.apple.mpegurl')) {
        refVideo.src = srcHls;
    } else {
        refVideo.src = videoSrc;
    }

    refVideo.load();

    return null;
};

const configLoaded = (config, param) => {
    const { controller } = param;

    loadVideo(config.src, param).then((player) => {
        controller.current = new VideoController(config, param.refVideo, player, param.props.options);
        controller.current.InitPoster();
    });
};

const changePlayState = (controller, continueVideo, pause) => {
    if (!controller.current) return;

    if (pause) {
        if (continueVideo.current) return;

        // eslint-disable-next-line no-param-reassign
        continueVideo.current = controller.current.isPlaying;
        if (controller.current.isPlaying) controller.current.Pause();
    } else {
        if (continueVideo.current) controller.current.Play();
        // eslint-disable-next-line no-param-reassign
        continueVideo.current = false;
    }
};

const checkPlayState = (criterias, controller, continueVideo) => {
    if (criterias.indexOf(false) > -1) {
        changePlayState(controller, continueVideo, true);
    } else {
        changePlayState(controller, continueVideo, false);
    }
};

const init = (param) => {
    const { src, configPath } = param.props;

    if (configPath) {
        // lazy load our video config.
        VideoControllerUtils.GetConfig(configPath, (config) => {
            configLoaded(config, param);
        });
    } else if (src) {
        configLoaded({ src }, param);
    }

    document.documentElement.addEventListener('fullscreenchange', () => {
        param.setFullscreenState(document.fullscreenElement !== null);
    });
};

const kill = (controller) => {
    if (controller.current) controller.current.Kill();
};

const Video = (props) => {
    const { poster } = props;
    const [fullscreenState, setFullscreenState] = useState(true);
    const continueVideo = useRef(false);
    const controller = useRef(null);
    const videoContainer = useRef(null);
    let refVideo = null;
    const store = useContext(StateContext);
    const { state } = store;

    useEffect(() => {
        init({
            props,
            refVideo,
            controller,
            continueVideo,
            fullscreenState,
            setFullscreenState,
        });

        // returned function will be called on component unmount
        return () => {
            kill(controller);

            if (videoContainer.current) {
                const videoElem = videoContainer.current.querySelector('video');
                videoElem.pause();

                while (videoElem.firstChild) {
                    videoElem.removeChild(videoElem.lastChild);
                }

                videoElem.removeAttribute('src');
                videoElem.load();

                videoContainer.current.removeChild(videoElem);

                if (typeof window.CollectGarbage === 'function') {
                    window.CollectGarbage();
                }
            }
        };
    }, []);

    checkPlayState(
        [
            fullscreenState,
            !state.fullscreenOverlayVisible,
            !state.fullscreenOverlayScrollVisible,
            !state.disclaimerOverlayVisible,
            !state.registerInterestOverlayVisible,
            !state.deviceOverlayVisible,
        ],
        controller,
        continueVideo
    );

    const posterAttr = poster ? { poster } : {};
    const { offset, offsetRatio } = props;
    const currentRatio = window.innerWidth / window.innerHeight;
    const offsetStyle = {
        bottom: currentRatio > offsetRatio ? `calc(-100% + ${offset}px)` : '-100%',
    };

    return (
        <div className="videoContainer" style={{ width: window.innerWidth, height: window.innerHeight }} data-testid="elementVideo" ref={videoContainer}>
            <video
                muted
                preload="auto"
                playsInline
                ref={(obj) => {
                    refVideo = obj;
                }}
                {...posterAttr}
                style={offsetStyle}
            />
        </div>
    );
};

export default Video;
